
import React from 'react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import FadeInSection from '@/components/ui/FadeInSection';
import { Button } from '@/components/ui/button';
import { Shield, Zap, Users } from 'lucide-react';
import GlassCard from '@/components/ui/GlassCard';
import { useLanguage } from '@/components/i18n/language-provider';

const About = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow">
        {/* Hero Section with same styling as home page hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 keystle-dot-pattern -z-10 opacity-50"></div>
          <div className="absolute inset-0 bg-gradient-radial from-transparent to-background -z-10"></div>
          
          <div className="container mx-auto px-6">
            <FadeInSection>
              <div className="mb-12 max-w-4xl mx-auto text-center">
                <div className="inline-block mb-5">
                  <span className="rounded-full px-3 py-1 text-sm bg-primary/10 text-primary font-medium">
                    {t('about.aboutKeystle')}
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">Empowering A Passwordless Tomorrow</h1>
                <p className="text-xl md:text-2xl text-brand-pink font-semibold mb-10">
                  {t('about.heroSubtitle')}
                </p>
              </div>
            </FadeInSection>
          </div>
          
          <div className="absolute -bottom-10 -left-10 h-64 w-64 rounded-full bg-brand-pink/10 filter blur-3xl opacity-70 -z-10"></div>
          <div className="absolute -top-10 -right-10 h-64 w-64 rounded-full bg-brand-purple/10 filter blur-3xl opacity-70 -z-10"></div>
        </section>

        {/* Core Features - Using the same grid layout and card style as home features */}
        <section className="py-10 relative">
          <div className="container mx-auto px-6">
            <FadeInSection delay={200}>
              <div className="mb-12 max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold mb-6">{t('about.introTitle')}</h2>
                <div className="space-y-4">
                  <p className="text-lg">
                    In the digital age, identity is more than just a login it is the key to secure, seamless, and frictionless experiences. However, traditional authentication methods are flawed: passwords are inconvenient, vulnerable, and compromise both security and user experience.
                  </p>
                  <p className="text-lg">
                    {t('about.introPara2')}
                  </p>
                  <p className="text-xl font-semibold text-brand-pink">
                    {t('about.introHighlight')}
                  </p>
                </div>
              </div>
              
              <div className="mb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <FadeInSection delay={0} direction="up">
                    <GlassCard className="h-full p-8 text-center" hoverEffect>
                      <div className="mb-4 p-2 w-16 h-16 flex items-center justify-center rounded-full bg-brand-pink/10 mx-auto">
                        <Shield className="w-8 h-8 text-brand-pink" />
                      </div>
                      <h3 className="text-xl font-bold mb-4">Passwordless security</h3>
                      <p className="text-muted-foreground">
                        {t('about.feature1Desc')}
                      </p>
                    </GlassCard>
                  </FadeInSection>
                  
                  <FadeInSection delay={100} direction="up">
                    <GlassCard className="h-full p-8 text-center" hoverEffect>
                      <div className="mb-4 p-2 w-16 h-16 flex items-center justify-center rounded-full bg-brand-purple/10 mx-auto">
                        <Users className="w-8 h-8 text-brand-purple" />
                      </div>
                      <h3 className="text-xl font-bold mb-4">Seamless integration</h3>
                      <p className="text-muted-foreground">
                        {t('about.feature2Desc')}
                      </p>
                    </GlassCard>
                  </FadeInSection>
                  
                  <FadeInSection delay={200} direction="up">
                    <GlassCard className="h-full p-8 text-center" hoverEffect>
                      <div className="mb-4 p-2 w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mx-auto">
                        <Zap className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-4">AI-Driven technology</h3>
                      <p className="text-muted-foreground">
                        {t('about.feature3Desc')}
                      </p>
                    </GlassCard>
                  </FadeInSection>
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>

        {/* Our History Section */}
        <section className="py-10 relative">
          <div className="container mx-auto px-6">
            <FadeInSection delay={300}>
              <div className="mb-16 bg-secondary/5 rounded-xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('about.historyTitle')}</h2>
                  <div className="w-24 h-1 bg-brand-pink mx-auto mb-4"></div>
                </div>
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-xl font-bold mb-4">{t('about.historyHeading')}</h3>
                  <h4 className="text-lg font-medium text-brand-pink mb-4">{t('about.historySubheading')}</h4>
                  <div className="space-y-4 text-lg">
                    <p>
                      {t('about.historyPara1')}
                    </p>
                    <p>
                      {t('about.historyPara2')}
                    </p>
                    <p className="font-semibold">{t('about.historyHighlight')}</p>
                    
                    <h4 className="text-lg font-medium text-brand-pink mt-8 mb-4">{t('about.visionHeading')}</h4>
                    <p>
                      {t('about.visionPara1')}
                    </p>
                    <p>
                      {t('about.visionPara2')}
                    </p>
                    <p>
                      {t('about.visionPara3')}
                    </p>
                    <p>
                      {t('about.visionPara4')}
                    </p>
                  </div>
                  <div className="mt-8 p-6 bg-brand-pink/5 rounded-lg border border-brand-pink/10">
                    <p className="text-lg italic">
                      "{t('about.quote')}"
                    </p>
                    <p className="mt-4">
                      {t('about.quoteContext')}
                    </p>
                    <p className="mt-3 text-sm text-right text-muted-foreground italic">
                      {t('about.quoteAttribution')}
                    </p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>
        
        {/* Purpose & Principles Section - Using the same grid layout as home */}
        <section className="py-10 relative">
          <div className="container mx-auto px-6">
            <FadeInSection delay={400}>
              <div className="mb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <FadeInSection delay={0} direction="left">
                    <div className="bg-gradient-to-br from-secondary/10 to-primary/5 p-8 rounded-xl">
                      <h2 className="text-2xl font-bold mb-2">{t('about.purposeTitle')}</h2>
                      <p className="text-lg text-brand-pink font-medium mb-4">{t('about.purposeSubtitle')}</p>
                      <p className="text-lg">
                        {t('about.purposeText')}
                      </p>
                    </div>
                  </FadeInSection>
                  
                  <FadeInSection delay={100} direction="right">
                    <div className="bg-gradient-to-br from-primary/10 to-secondary/5 p-8 rounded-xl">
                      <h2 className="text-2xl font-bold mb-2">{t('about.principlesTitle')}</h2>
                      <p className="text-lg text-brand-pink font-medium mb-4">{t('about.principlesSubtitle')}</p>
                      <p className="text-lg">
                        {t('about.principlesText')}
                      </p>
                    </div>
                  </FadeInSection>
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>
        
        {/* CTA Section - Matching home page style but with Contact Us instead of Get Started */}
        <section className="py-10 relative">
          <div className="container mx-auto px-6">
            <FadeInSection delay={500}>
              <div className="text-center p-10 bg-gradient-to-r from-brand-purple/20 via-primary/20 to-brand-purple/20 rounded-xl">
                <h2 className="text-3xl font-bold mb-6">{t('about.ctaTitle')}</h2>
                <Button 
                  size="lg" 
                  className="px-8 py-6 font-medium text-lg transition-transform duration-300 hover:scale-105 bg-brand-pink hover:bg-brand-pink/90 shadow-md hover:shadow-lg"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  {t('nav.contactUs')}
                </Button>
              </div>
            </FadeInSection>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
